// Pure readouts for the Jira connector panel (AL-127). No DOM / state imports,
// so this is unit-testable under Node — the same split router-url.ts uses.
//
// Everything here answers one admin question: "is this thing actually working?"
// The connector fails in halves and fails silently, so the panel's job is to
// name which half is dead. Two independent tiers (design §5):
//
//   webhooks  siteUrl + webhookSecret   freshness — Jira tells us a ticket moved
//   rest      siteUrl + apiEmail + apiToken   detail fetch + 404 validation
//
// A connector can be half-configured indefinitely with no error anywhere: the
// worker's `jiraFromSettings` returns null and `fetchDetails` exits on its first
// line, so tickets simply arrive with no title and nobody is told why. These
// functions turn that silence into a sentence.

/** The connector shape the GET returns (`publicJira` in routes/connectors.ts). */
export interface PublicJira {
  siteUrl: string | null;
  apiEmail: string | null;
  lastWebhookAt: string | null;
  webhookSecretSet: boolean;
  apiTokenSet: boolean;
  webhooksConfigured: boolean;
  pullConfigured: boolean;
  webhookPath: string | null;
}

// How long without a delivery before the panel calls the webhook stale. Jira
// only speaks when an issue changes, so silence is ambiguous by nature — a week
// is long enough that a working connector on a quiet site won't cry wolf, and
// short enough that a webhook someone deleted surfaces inside a sprint.
export var STALE_AFTER_MS = 7 * 24 * 60 * 60 * 1000;

export interface Health {
  // `off` = not configured; `waiting` = configured, nothing has arrived yet;
  // `live` = a recent delivery; `stale` = configured but silent too long.
  level: 'off' | 'waiting' | 'live' | 'stale';
  text: string;
}

/**
 * Webhook (freshness) tier health.
 *
 * `waiting` is deliberately NOT a warning: an admin who just pasted the URL has
 * no event yet, and flagging that as a problem would send them re-checking a
 * correct setup. It becomes `stale` only once a real interval has passed with
 * nothing — and `stale` is the same reading whether Jira never delivered at all
 * or stopped delivering, because from here those are the same fact.
 *
 * `nowMs` is passed in rather than read from the clock so this stays pure.
 */
export function webhookHealth(c: PublicJira, nowMs: number): Health {
  if (!c.webhooksConfigured) {
    return { level: 'off', text: c.siteUrl ? 'No webhook secret — generate one below.' : 'Not configured.' };
  }
  var last = c.lastWebhookAt ? Date.parse(c.lastWebhookAt) : NaN;
  // An unparseable stamp is treated as absent, not as ancient: the alternative
  // reading (epoch 0, therefore stale) would accuse a working connector.
  if (!c.lastWebhookAt || isNaN(last)) {
    return { level: 'waiting', text: 'Waiting for the first event from Jira.' };
  }
  var ageMs = nowMs - last;
  var ago = agoText(ageMs);
  if (ageMs > STALE_AFTER_MS) {
    return {
      level: 'stale',
      text: 'No events for ' + ago + '. Webhooks are the only thing keeping ticket status current — check the webhook in Jira.',
    };
  }
  return { level: 'live', text: 'Last event ' + ago + ' ago.' };
}

/** REST (detail-fetch) tier health. No liveness stamp exists for this tier. */
export function restHealth(c: PublicJira): Health {
  if (c.pullConfigured) return { level: 'live', text: 'Titles, statuses and story points are fetched for tickets your sessions name.' };
  if (!c.siteUrl) return { level: 'off', text: 'Not configured.' };
  var missing: string[] = [];
  if (!c.apiEmail) missing.push('account email');
  if (!c.apiTokenSet) missing.push('API token');
  // Named rather than "incomplete": this is the exact state that produces
  // untitled tickets, and the admin needs to know which field is empty.
  return { level: 'off', text: 'No ' + missing.join(' or ') + ' — tickets will have no title or status.' };
}

/**
 * The absolute URL to paste into Jira's webhook form.
 *
 * The server returns a PATH (it can't know which of a proxy's hostnames the
 * admin reached it on), and a path is useless in Jira's form — so the origin is
 * joined here, from the address the admin is looking at, which is by
 * construction one that works.
 */
export function webhookUrl(origin: string, path: string | null): string | null {
  if (!path) return null;
  return String(origin || '').replace(/\/+$/, '') + path;
}

/**
 * Whether Jira Cloud could actually reach this origin.
 *
 * Worth its own check because it is THE first-run failure and it looks like
 * nothing: an admin developing against localhost pastes a perfectly-formed URL
 * into Jira, Jira's delivery fails on its own servers, and this panel just sits
 * at `waiting` forever with no error to read.
 */
export function reachableFromJira(origin: string): boolean {
  var host = String(origin || '').replace(/^[a-z]+:\/\//i, '').split(/[:/]/)[0].toLowerCase();
  if (!host) return false;
  if (host === 'localhost' || host === '127.0.0.1' || host === '::1' || host === '0.0.0.0') return false;
  if (/\.local(host)?$/.test(host)) return false;
  // RFC1918 / link-local. Not exhaustive (a VPN-only DNS name is unreachable too
  // and looks public), so the panel words this as a caution, not a verdict.
  return !/^(10\.|127\.|169\.254\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.)/.test(host);
}

/** Coarse elapsed-time label. Days are enough — this reads as prose, not a clock. */
export function agoText(ms: number): string {
  if (!(ms > 0)) return 'just now';
  var mins = Math.floor(ms / 60000);
  if (mins < 1) return 'less than a minute';
  if (mins < 60) return mins + (mins === 1 ? ' minute' : ' minutes');
  var hours = Math.floor(mins / 60);
  if (hours < 24) return hours + (hours === 1 ? ' hour' : ' hours');
  var days = Math.floor(hours / 24);
  return days + (days === 1 ? ' day' : ' days');
}
